import type { ChatMessage } from '@repo/types';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

interface UseTinyAIScrollOrchestratorOptions {
  messages: ChatMessage[];
  isLoadingHistory: boolean;
  isLoadingMoreHistory: boolean;
  hasMoreHistory: boolean;
  loadMoreHistory: () => void;
}

export const useTinyAIScrollOrchestrator = ({
  messages,
  isLoadingHistory,
  isLoadingMoreHistory,
  hasMoreHistory,
  loadMoreHistory,
}: UseTinyAIScrollOrchestratorOptions) => {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const prevScrollHeightRef = useRef<number>(0);
  const didPrependRef = useRef<boolean>(false);
  const userHasScrolledUpRef = useRef<boolean>(false);
  const justLoadedMoreHistoryRef = useRef<boolean>(false);
  const hasScrolledToBottomInitiallyRef = useRef<boolean>(false);
  const lastUserMessageIdRef = useRef<string | null>(null);

  const handleScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;

    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 50;
    userHasScrolledUpRef.current = !isAtBottom;

    if (el.scrollTop <= 100 && hasMoreHistory && !isLoadingMoreHistory) {
      prevScrollHeightRef.current = el.scrollHeight;
      didPrependRef.current = true;
      justLoadedMoreHistoryRef.current = true;
      loadMoreHistory();
    }
  }, [hasMoreHistory, isLoadingMoreHistory, loadMoreHistory]);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Anchor the latest user message to the top. Dedup by id so streaming chunks
  // (which mutate the assistant bubble) don't retrigger a scroll.
  useLayoutEffect(() => {
    if (didPrependRef.current || justLoadedMoreHistoryRef.current) return;

    const container = messagesContainerRef.current;
    if (!container) return;

    let latestUserId: string | null = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].senderType === 'user') {
        latestUserId = messages[i].id;
        break;
      }
    }

    if (latestUserId === null) return;
    if (latestUserId === lastUserMessageIdRef.current) return;

    lastUserMessageIdRef.current = latestUserId;

    // Initial paint of historical messages is handled by the scroll-to-bottom
    // effect below.
    if (!hasScrolledToBottomInitiallyRef.current) return;

    // Defer a frame so flex resolution settles (greeting unmount resizes
    // topSection in the same commit) before we read clientHeight to size
    // the spacer.
    const rafId = requestAnimationFrame(() => {
      const liveContainer = messagesContainerRef.current;
      if (!liveContainer) return;

      const node = liveContainer.querySelector<HTMLElement>(
        `[data-message-id="${latestUserId}"]`,
      );
      const spacer = liveContainer.querySelector<HTMLElement>(
        '[data-bottom-spacer]',
      );
      if (spacer && node) {
        // Subtract what's already below the anchor (user message + loader)
        // so the spacer only fills the unused viewport, not over-counting.
        let belowHeight = node.offsetHeight;
        let el = node.nextElementSibling as HTMLElement | null;
        while (el && !el.matches('[data-bottom-spacer]')) {
          belowHeight += el.offsetHeight;
          el = el.nextElementSibling as HTMLElement | null;
        }
        spacer.style.minHeight = `${Math.max(0, liveContainer.clientHeight - belowHeight)}px`;
      }

      node?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => cancelAnimationFrame(rafId);
  }, [messages]);

  // Preserve scroll position when older messages are prepended.
  useLayoutEffect(() => {
    const el = messagesContainerRef.current;
    if (!el || !didPrependRef.current) return;
    el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
    didPrependRef.current = false;
    justLoadedMoreHistoryRef.current = false;
  }, [messages.length]);

  // Scroll to bottom on first load of a conversation. Flag resets when
  // isLoadingHistory flips back to true (account switch).
  useEffect(() => {
    if (isLoadingHistory) {
      hasScrolledToBottomInitiallyRef.current = false;
      lastUserMessageIdRef.current = null;
      return;
    }
    if (hasScrolledToBottomInitiallyRef.current) return;
    hasScrolledToBottomInitiallyRef.current = true;
    if (messages.length === 0) return;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].senderType === 'user') {
        lastUserMessageIdRef.current = messages[i].id;
        break;
      }
    }
    messagesEndRef.current?.scrollIntoView({
      behavior: 'auto',
      block: 'end',
    });
  }, [isLoadingHistory, messages.length]);

  const resetScrollLockOnSend = useCallback(() => {
    userHasScrolledUpRef.current = false;
  }, []);

  return {
    messagesContainerRef,
    messagesEndRef,
    resetScrollLockOnSend,
  };
};
